import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useStore } from 'vuex';

import GlobalRequest from '@/api/global-request';
import { useMetricComposable } from '@/composables/MetricComposable';
import { formatDate } from '@/helpers/date-formatter';
import { showToast } from '@/helpers/toast-handler';

import router from '@/router';
import { getPage, getPerPage } from '@/composables/PaginationComposable';

const DASHBOARD_URL = 'v2/datasource/dashboard';
const DATASOURCE_DASHBOARD_URL = 'v2/datasource/dashboard';

export const useDashboardStore = defineStore('dashboard', () => {
    const store = useStore();
    const company_id = computed(() => {
        return store.getters['auth/userCompanyId']?.id;
    });

    const { getMetricDetailsById, loadMetricReportData } = useMetricComposable();
    const loading = ref(false);
    // states
    const dashboard = ref({});
    const dashboards = ref([]);
    const totalDashboards = ref(null);
    const dashboardPreview = ref([]);

    // actions
    const getDSDashboards = async (params, list) => {
        try {

            let page_size = getPerPage(list)
            let page = getPage(list)

            params.page_size = page_size
            params.page = page + 1
            params.paginate = true

            await GlobalRequest.getRequest(DATASOURCE_DASHBOARD_URL, {
                params: {
                    ...params,
                    company_id: company_id.value,
                },
            }).then((response) => {
                totalDashboards.value = response.data.total

                dashboards.value = response.data?.data?.map((dashboard) => {
                    return {
                        ...dashboard,
                        metrics_count: dashboard?.details?.length,
                        created_at: formatDate(dashboard.created_at),
                        default_dashboard: dashboard?.default ? 'True' : 'False',
                    };
                });
                return dashboards;
            });
        } catch (err) {
            showToast(err.response, 'Failed to fetch all dashboards');
        }
    };

    const addDashboard = async (params) => {
        try {
            const formattedMetrics = params?.metrics?.map((metricId, index) => {
                return {
                    order: index + 1,
                    metric_id: metricId,
                };
            });
            await GlobalRequest.postRequest(DATASOURCE_DASHBOARD_URL, {
                company_id: company_id.value,
                name: params.name,
                code: params.code,
                description: params.description,
                default: false,
                order: true,
                metrics: formattedMetrics,
            }).then((res) => {
                if (res.statusCode === 201) {
                    showToast(res, 'Dashboard added');
                    // after basic dashboard info is added,
                    // redirect to a page of that dashboard where you can add metric
                    setTimeout(() => {
                        router.push({ name: 'EditDashboard', params: { id: res.data?.id } });
                    }, 1500);
                } else {
                    showToast(res, 'Something went wrong');
                }
            });
        } catch (err) {
            showToast(err.response, 'Failed to add dashboard');
        }
    };

    // API for this doesnt exist so it wont work
    const getDashboardDetails = async (id) => {
        try {
            await GlobalRequest.getRequest(`${DATASOURCE_DASHBOARD_URL}/${id}`, {
                params: {
                    company_id: company_id.value,
                },
            }).then((res) => {
                dashboard.value = loadEditDashboard(res.data);
                // dashboard.value = res.data;
                return res.data;
            });
        } catch (err) {
            showToast(err.response, 'Failed to fetch dashboard details');
        }
    };

    const updateDashboard = async (id, body) => {
        try {
            await GlobalRequest.patchRequest(`${DATASOURCE_DASHBOARD_URL}/${id}?company_id=${company_id.value}`, {
                ...body,
                metrics: body.metrics?.map((metric) => {
                    return { order: metric.order, metric_id: metric.metric_id };
                }),
            }).then((res) => {
                if (res.statusCode === 200) {
                    showToast(res, 'Dashboard updated successfully');
                    // redirect to manage dashboards
                    setTimeout(() => {
                        router.push({ name: 'ManageDashboard' });
                    }, 2000);
                } else {
                    showToast(res, 'Something went wrong');
                }
            });
        } catch (err) {
            showToast(err.response, 'Failed to update dashboard');
        }
    };

    const deleteDashboard = async (id) => {
        try {
            await GlobalRequest.deleteRequest(`${DASHBOARD_URL}/${id}`, {
                params: {
                    company_id: company_id.value,
                },
            }).then((res) => {
                if (res.statusCode === 200) {
                    showToast(res, 'Dashboard deleted');
                    // redirect to manage dashboards
                } else {
                    showToast(res, 'Something went wrong');
                }
            });
            // await getDashboards();
        } catch (err) {
            showToast(err.response, 'Failed to delete dashboard');
        }
    };

    const setDefaultDashboard = async (dashboardId) => {
        try {
            // AND AGAIN, we dont have fetch ID details feature on the API so we have to pull all the dashboards
            await getDSDashboards({ paginate: false });

            // Find dashboard by id
            const dashboard = dashboards.value.find((dashboard) => dashboard.id === dashboardId);

            // Only for company dashboards at the moment
            if (dashboard) {
                // now fetch the dashboard details
                const dashboardBody = {
                    company_id: dashboard.company_id,
                    name: dashboard.name,
                    code: dashboard.code,
                    default: true, // setting it true to make it default
                    order: true, // this is not mentioned in the documentation, please fix it there, thanks
                    description: dashboard.description,
                    metrics: dashboard.details?.map((metric) => {
                        return {
                            order: metric.order,
                            metric_id: metric.metric_id,
                        };
                    }),
                };
                // Now update this details to make it default
                await updateDashboard(dashboardId, dashboardBody);
            }
        } catch (err) {
            showToast(err.response, 'Failed to set dashboard as default');
        }
    };

    // This wont work for DS report
    const loadDashboardMetricsReport = async (dashboardId) => {
        // console.log(dashboardId);
        // initate the loader
        loading.value = true;
        // empty the dashboardPreview before starting to push metrics data
        dashboardPreview.value = [];
        // extract the dashboard details from the dashboards array
        // initially check in the dashboards state
        dashboard.value = dashboards.value?.find((dashboard) => dashboard.id === dashboardId);
        // if its null then extract dashboard details directly
        if (!dashboard.value) {
            await getDashboardDetails(dashboardId);
            // console.log(dashboard.value);
        }
        // if there is a dashboard
        if (dashboard.value) {
            // extract metrics id
            const metrics = dashboard.value?.metrics?.map((metric) => metric.metric_id);
            // console.log(metrics);
            //  now load metrics details for each metrics and load the report
            for (let i = 0; i < metrics.length; i++) {
                const metricDetails = {};
                metricDetails.id = metrics[i];
                // console.log('Loading metric details for metric id: ', metricDetails.id);
                // debugger;
                const metric = await getMetricDetailsById(metrics[i]);
                metricDetails.name = metric?.name;
                // extract only display format from metric details
                metricDetails.displayFormat = metric?.display_format ?? 'table';
                // console.log('Setting up display format');
                metricDetails.reportData = await loadMetricReportData(metrics[i]);
                // console.log(metrics[i], metricDetails.reportData);
                dashboardPreview.value?.push(metricDetails);
                // console.log('pushing into dashboard preview');
            }
            // console.log(dashboardPreview.value);
        }

        // set loading to false at the end
        loading.value = false;
    };

    const initDashboardDetails = () => {
        dashboard.value = {};
        // dashboards.value = [];
        // defaultDashboardId.value = null;
        dashboardPreview.value = [];
    };

    // Currently users are not arranged, make it user
    function loadEditDashboard(data) {
        dashboard.value = {
            ...data,
            metrics: data.details?.map((item) => {
                return {
                    order: item.order,
                    metric_id: item.metric_id,
                    // name: item.metric.name, // For some reason there was no metric name so we will leave this commented
                    // description: item.metric.description, // Same for metric description
                    // display_format: item.metric.display_format, // Cant find format as well so commented
                };
            }),
        };
    }

    // Change metrics orders
    function changeMetricOrders(newOrders) {
        dashboard.value.metrics = newOrders?.map((obj, index) => {
            return {
                ...obj,
                order: index,
            };
        });
    }

    function addMetricInDashboard(metric) {
        const latestOrder = dashboard.value?.metrics?.length;
        dashboard.value?.metrics?.push({
            order: latestOrder + 1,
            metric_id: metric?.id,
            name: metric?.name,
            description: metric?.description,
            display_format: metric?.display_format,
        });
    }

    function removeMetricFromDashboard(metricId) {
        dashboard.value.metrics = dashboard.value?.metrics?.filter((metric) => metric.metric_id !== metricId);
    }

    return {
        loading,
        // states
        dashboard,
        dashboards,
        totalDashboards,
        dashboardPreview,
        // actions
        getDSDashboards,
        addDashboard,
        getDashboardDetails,
        updateDashboard,
        deleteDashboard,
        setDefaultDashboard,
        loadDashboardMetricsReport, // loads metrics information of a dashboard to show in chart
        initDashboardDetails,
        // helpers
        changeMetricOrders, // change metrics orders in dashboard edit
        addMetricInDashboard,
        removeMetricFromDashboard,
        loadEditDashboard,
    };
});
